// frontend/src/utils/chatAttachmentApi.js
// API functions for chat file attachments

import secureAxios from "./secureAxios";

const API_BASE = "/api/chat/attachments";

/**
 * Get a signed URL for uploading a file
 */
export const getUploadUrl = async (file, conversationId = null) => {
  const response = await secureAxios.post(`${API_BASE}/upload-url`, {
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    conversationId,
  });
  return response.data;
};

/**
 * Upload a file directly to cloud storage using signed URL
 */
export const uploadFileToStorage = async (
  uploadUrl,
  file,
  onProgress = null
) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ success: true });
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Upload failed"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Upload aborted"));
    });

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.send(file);
  });
};

/**
 * Confirm upload completed
 */
export const confirmUpload = async (attachmentId) => {
  const response = await secureAxios.post(
    `${API_BASE}/confirm/${attachmentId}`
  );
  return response.data;
};

/**
 * Complete upload flow: get URL, upload, confirm
 */
export const uploadAttachment = async (
  file,
  conversationId = null,
  onProgress = null
) => {
  // Step 1: Get signed upload URL
  const { uploadUrl, attachmentId } = await getUploadUrl(file, conversationId);

  // Step 2: Upload file to storage
  await uploadFileToStorage(uploadUrl, file, onProgress);

  // Step 3: Confirm upload
  const result = await confirmUpload(attachmentId);

  return {
    ...result,
    attachmentId,
  };
};

/**
 * Get download URL for an attachment
 */
export const getDownloadUrl = async (attachmentId) => {
  const response = await secureAxios.get(
    `${API_BASE}/download/${attachmentId}`
  );
  return response.data;
};

/**
 * Get preview URL for an image attachment
 */
export const getPreviewUrl = async (attachmentId) => {
  const response = await secureAxios.get(`${API_BASE}/preview/${attachmentId}`);
  return response.data;
};

/**
 * Delete an attachment
 */
export const deleteAttachment = async (attachmentId) => {
  const response = await secureAxios.delete(`${API_BASE}/${attachmentId}`);
  return response.data;
};

/**
 * Get all attachments for a conversation
 */
export const getConversationAttachments = async (
  conversationId,
  params = {}
) => {
  const response = await secureAxios.get(
    `${API_BASE}/conversation/${conversationId}`,
    { params }
  );
  return response.data;
};

/**
 * Download a file
 */
export const downloadAttachment = async (attachmentId) => {
  const { downloadUrl, fileName } = await getDownloadUrl(attachmentId);

  // Create temporary link and trigger download
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// File validation helpers
export const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const validateFile = (file) => {
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      valid: false,
      error:
        "File type not allowed. Allowed: images, PDF, Word, Excel, text files",
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`,
    };
  }

  return { valid: true };
};

export const getFileIcon = (mimeType) => {
  if (!mimeType) return "bi-file-earmark";
  if (mimeType.startsWith("image/")) return "bi-file-image";
  if (mimeType === "application/pdf") return "bi-file-pdf";
  if (mimeType.includes("word")) return "bi-file-word";
  if (mimeType.includes("excel") || mimeType.includes("spreadsheet"))
    return "bi-file-excel";
  if (mimeType.startsWith("text/")) return "bi-file-text";
  return "bi-file-earmark";
};

export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export default {
  uploadAttachment,
  getDownloadUrl,
  getPreviewUrl,
  deleteAttachment,
  getConversationAttachments,
  downloadAttachment,
  validateFile,
  getFileIcon,
  formatFileSize,
  ALLOWED_MIME_TYPES,
  MAX_FILE_SIZE,
};
